import React from 'react';
import { T } from '../customer/customers.theme';
import {
  Dialog,
  DialogHeader,
  ErrorLine,
  PillField,
  PrimaryButton,
} from '../customer/CustomersUI';
import { SelectField } from '../menu/MenuUI';
import { printerIdOf } from '../menu/menuApi';

/**
 * A category, and the printer its tickets come out of.
 *
 * The phone's dialog: a name, a printer, one button. Its wording — `Add New
 * Category`, `Geli Magaca`, `Ku Dar`, `Update Category`, `Update`.
 *
 * The printer list is read-only here. Printers are added on the phone, where
 * the person doing it is standing next to the machine with its IP address.
 */

const COPY = {
  addTitle: 'Add New Category',
  editTitle: 'Update Category',
  name: 'Geli Magaca',
  printer: 'Printer',
  noPrinter: 'No ticket',
  add: 'Ku Dar',
  save: 'Update',
  nameRequired: 'Name is required',
  failed: 'Could not save. Try again.',
  noPrinters: 'No printers have been set up yet. Add them on the phone.',
};

export default function CategoryDialog({ category, printers, businessId, onClose, onSubmit }) {
  const editing = Boolean(category);

  const [name, setName] = React.useState(() => (category && category.name) || '');
  const [printer, setPrinter] = React.useState(() => printerIdOf(category));

  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');

  const submit = async () => {
    if (busy) return;

    if (!name.trim()) {
      setError(COPY.nameRequired);
      return;
    }

    setBusy(true);
    setError('');

    try {
      await onSubmit({
        category,
        payload: {
          name: name.trim(),
          // null, not '': the server reads null as "unmap this category" and
          // an id as "point it here". Never omitted, because the form shows a
          // value and a form should write what it shows.
          printer: printer || null,
          ...(editing ? {} : { business: businessId }),
        },
      });
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || COPY.failed);
      setBusy(false);
    }
  };

  return (
    <Dialog onClose={onClose} locked={busy} width={400}>
      <div style={{ padding: '22px 24px 24px' }}>
        <DialogHeader
          title={editing ? COPY.editTitle : COPY.addTitle}
          onClose={onClose}
          locked={busy}
        />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 18 }}>
          <PillField
            name="category-name"
            value={name}
            onChange={(value) => {
              setName(value);
              setError('');
            }}
            onEnter={submit}
            placeholder={COPY.name}
            disabled={busy}
            autoFocus
          />

          <SelectField
            name="category-printer"
            label={COPY.printer}
            value={printer}
            onChange={setPrinter}
            disabled={busy || !printers.length}
          >
            <option value="">{COPY.noPrinter}</option>
            {printers.map((entry) => (
              <option key={entry._id} value={entry._id}>
                {entry.name}
                {entry.isActive === false ? ' (off)' : ''}
              </option>
            ))}
          </SelectField>

          <span style={{ fontSize: 11.5, color: T.inkFaint, lineHeight: 1.5 }}>
            {printers.length
              ? 'Items in this category print their ticket here.'
              : COPY.noPrinters}
          </span>
        </div>

        <div style={{ paddingTop: 16 }}>
          <ErrorLine>{error}</ErrorLine>
        </div>

        <PrimaryButton
          onClick={submit}
          busy={busy}
          style={{ width: '100%', marginTop: error ? 12 : 20 }}
        >
          {editing ? COPY.save : COPY.add}
        </PrimaryButton>
      </div>
    </Dialog>
  );
}
